import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useController } from "react-hook-form";
import { dateToDbUtcIsoString, dateToLocalIsoString, formatDate, isDateValid } from "../utils/datetime";
import CustomFieldDropdown from "./CustomField/CustomFieldDropdown";
import CustomFieldDropdownList from "./CustomField/CustomFieldDropdownList";
import InfoIcon from "../assets/info_tooltip.svg";
import { useTranslation } from "react-i18next";

const DROPDOWN_FIELD_NAMES = ["country", "state", "preferred_language"];

function sortOptionsByLabel(options) {
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

function LabeledInput({ fieldInfo = {}, control, register, errors, prefix, userData, watchCountry }) {
  const { t } = useTranslation();
  const key = `${prefix}.${fieldInfo.internal_name}`;
  const fieldValue = userData ? userData[fieldInfo.internal_name] : "";
  const { field } = useController({ name: key, control });

  const [currentValue, setCurrentValue] = useState(fieldValue);

  const defaultOptions = fieldValue ? [{ value: fieldValue, label: fieldValue }] : [];

  // for dropdowns with dynamic options
  const [asyncOptions, setAsyncOptions] = useState(defaultOptions);

  const [isMobile, setIsMobile] = useState(false);

  const prevCountryRef = useRef();

  useEffect(() => {
    if (fieldInfo.internal_name === "country") {
      // if it's a country field, fetch country options
      async function getCountryOptions() {
        try {
          const response = await axios.get("/api/constants/countries");
          const countryEntries = Object.entries(response.data);
          const mappedOptions = countryEntries.map(([value, label]) => ({
            value,
            label,
          }));
          setAsyncOptions(mappedOptions);
        } catch (e) {
          console.error(e);
        }
      }
      getCountryOptions();
    } else if (fieldInfo.internal_name === "preferred_language") {
      // if it's a preferred_language field, fetch language options
      async function getLanguageOptions() {
        try {
          const response = await axios.get("/api/user_languages");
          const mappedOptions = response.data.map((lang) => ({
            value: lang.id,
            label: lang.language,
          }));
          setAsyncOptions(mappedOptions);
        } catch (e) {
          console.error(e);
        }
      }
      getLanguageOptions();
    }
  }, [fieldInfo]);

  useEffect(() => {
    if (fieldInfo.internal_name !== "state") return;
    // if it's a state field, fetch state options based on country
    async function getStateOptions(countryCode) {
      if (!countryCode) return;
      try {
        const response = await axios.get("/api/constants/states", {
          params: { country_code: countryCode },
        });
        const stateEntries = Object.entries(response.data);
        const mappedOptions = stateEntries.map(([value, label]) => ({
          value,
          label,
        }));
        setAsyncOptions(sortOptionsByLabel(mappedOptions));
      } catch (e) {
        console.error(e);
        setAsyncOptions([]);
      }
      prevCountryRef.current = countryCode;
    }
    if (watchCountry && watchCountry !== prevCountryRef.current) {
      // fetch states when country changes and is different from previous
      getStateOptions(watchCountry);
    } else if (!watchCountry && prevCountryRef.current) {
      // reset state field when country is cleared
      prevCountryRef.current = null;
      setAsyncOptions([]);
      field.onChange("");
    } else if (!watchCountry && userData?.country) {
      // initial load with existing userData
      getStateOptions(userData.country);
    }
  }, [watchCountry]);

  useEffect(() => {
    // if it's a dropdown, we need to set the value to the field
    if (fieldInfo.is_extended && fieldInfo.field_type === "dropdown" && fieldValue?.length) {
      field.onChange(fieldValue);
    }
  }, [fieldInfo, fieldValue]);

  useEffect(() => {
    // https://stackdiary.com/detect-mobile-browser-javascript
    if (/Android|iPhone/i.test(navigator.userAgent)) {
      setIsMobile(true);
    }
  }, []);

  const label = (
    <label className="label" htmlFor={key} data-cy={key}>
      {fieldInfo.label + (fieldInfo.required ? "*" : "")}
      {fieldInfo.tooltip && (
        <div className="tooltip">
          <InfoIcon />
          <span className="tooltiptext">{fieldInfo.tooltip}</span>
        </div>
      )}
    </label>
  );

  let errArr = errors;
  prefix.split(".").forEach((x) => (errArr = errArr[x] ? errArr[x] : errArr));

  const userFieldErrors =
    errArr[prefix] && errArr[key]
      ? [
          errArr[prefix] && errArr[prefix][fieldInfo.internal_name] && errArr[prefix][fieldInfo.internal_name].message,
          errArr[key] && errArr[key].message,
          errArr[fieldInfo.internal_name] && errArr[fieldInfo.internal_name].message,
        ]
      : [];

  // shared props for all input cases
  const commonInputProps = {
    id: key,
    name: key,
    "data-cy": key,
    defaultValue: fieldValue,
    disabled: fieldInfo.admin_only,
    ...register(key, {
      required: fieldInfo.required && `${fieldInfo.label}${t("labeled_input.is_required_suffix")}`,
    }),
  };

  if (fieldInfo.field_type === "dropdown" && fieldInfo.is_extended) {
    if (fieldInfo.choices?.length < 10) {
      return (
        <CustomFieldDropdownList
          key={key}
          control={control}
          label={label}
          fieldInfo={fieldInfo}
          field={field}
          fieldValue={fieldValue}
          isMobile={isMobile}
          userFieldErrors={userFieldErrors}
          commonInputProps={commonInputProps}
        />
      );
    } else {
      return (
        <CustomFieldDropdown
          key={key}
          control={control}
          label={label}
          fieldInfo={fieldInfo}
          field={field}
          fieldValue={fieldValue}
          isMobile={isMobile}
          userFieldErrors={userFieldErrors}
          commonInputProps={commonInputProps}
        />
      );
    }
  } else if (["dropdown", "checkbox"].includes(fieldInfo.field_type) || DROPDOWN_FIELD_NAMES.includes(fieldInfo.internal_name)) {
    let choices = [];

    if (fieldInfo.field_type === "checkbox") {
      choices = [
        { value: "true", label: t("labeled_input.true") },
        { value: "false", label: t("labeled_input.false") },
      ];
    } else if (DROPDOWN_FIELD_NAMES.includes(fieldInfo.internal_name)) {
      choices = asyncOptions;
    } else {
      choices = fieldInfo.choices ? fieldInfo.choices : choices;
    }

    return (
      <div data-cy={key}>
        {label}
        <select {...commonInputProps}>
          <option />
          {choices.map((opt) => (
            <option key={`${key}.${opt.value}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="errorlist" data-cy={`error-${key}`}>
          {userFieldErrors.map((err, i) => (
            <div key={i}> {err} </div>
          ))}
        </div>
      </div>
    );
  } else if (fieldInfo.field_type === "date") {
    return (
      <div>
        {label}
        <input
          {...commonInputProps}
          type={fieldInfo.field_type}
          defaultValue={fieldValue !== "" ? formatDate(fieldValue) : fieldValue}
          placeholder={t("labeled_input.date_placeholder")}
          autoComplete="off"
        />
        <div className="errorlist" data-cy={`error-${key}`}>
          {userFieldErrors.map((err, i) => (
            <div key={i}> {err} </div>
          ))}
        </div>
      </div>
    );
  } else if (fieldInfo.field_type === "utc-datetime") {
    // converts UTC ISO string from DB to datetime-local
    const showValue = isDateValid(currentValue) ? dateToLocalIsoString(new Date(currentValue)) : "";

    const handleUtcDateTimeChange = (event) => {
      const localDateTimeString = event.target.value;
      // converts datetime-local string to back-end UTC ISO string
      const utcDateTimeString = isDateValid(localDateTimeString) ? dateToDbUtcIsoString(new Date(localDateTimeString)) : "";
      setCurrentValue(utcDateTimeString);
      field.onChange(utcDateTimeString);
    };

    delete commonInputProps.defaultValue;
    delete commonInputProps.onChange;
    delete commonInputProps.onBlur;

    return (
      <div>
        {label}
        <input {...commonInputProps} type="datetime-local" value={showValue} onChange={handleUtcDateTimeChange} autoComplete="off" />
        <div className="errorlist" data-cy={`error-${key}`}>
          {userFieldErrors.map((err, i) => (
            <div key={i}> {err} </div>
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div data-cy={key}>
        {label}
        <input {...commonInputProps} type={fieldInfo.field_type} autoComplete="off" />
        <div className="errorlist" data-cy={`error-${key}`}>
          {userFieldErrors.map((err, i) => (
            <div key={i}> {err} </div>
          ))}
        </div>
      </div>
    );
  }
}

export default LabeledInput;
